import AxL from "./axl";
import Ripple from './Ripple.js';
import ShareableCardMaker from './ShareableCardMaker.js';
import * as mobile from 'is-mobile';

const LIGHTNESS = "42%";
const LIGHTNESS_BUTTON = "25%";
const BACKGROUND_ALPHA = "0.359375";

const CS_DEBUG = false;

/**
 * Whether the user is in Culture Studio.
 */
const isCultureStudio = window.navigator.userAgent.toLowerCase().includes('culturestudio');
const isiOSWebview = window.navigator.userAgent.toLowerCase().includes('ioswebview');

export default class PoemPostcard {
  constructor() {
    this.assetsMap = new Map();

    this.artworkImg = document.getElementById("artwork-img");
    this.poem = document.getElementById("poem");
    this.backToEdit = document.getElementById("back-to-edit");
    this.poemAttribution = document.getElementById("poem-attribution");
    this.generateButton = document.getElementById("generate-button");
    this.poemTypeSelect = document.getElementById("poem-type-select");
    this.toneInput = document.getElementById("tone-input");
    this.toneWarning = document.getElementById("tone-warning");
    this.studioShareButton = document.getElementById("studio-share-button");
    this.studioSaveButton = document.getElementById("studio-save-button");
    this.shareRewrite = document.getElementById("share-rewrite");
    this.studioShareRewrite = document.getElementById("studio-share-rewrite");
    this.rewriteButton = document.getElementById("rewrite-button");
    this.studioRewriteButton = document.getElementById("studio-rewrite-button");
    this.moreArtButton = document.getElementById("more-artworks-button");
    this.shareMoreArtButton = document.getElementById("share-more-artworks-button");
    this.selectArtworkButton = document.getElementById("select-artwork-button");
    this.stampAmpersand = document.getElementById("stamp-ampersand");
    this.stampAmpersandMobile = document.getElementById(
      "stamp-ampersand-mobile"
    );
    this.poemErrorMessage = document.getElementById("poem-error-message");
    this.closePoemErrorButton = document.getElementById(
      "poem-error-message-close-button"
    );

    this.poemCleaned = "";
    this.dominantColor = "";

    this.loadArtworksStatus = "notloaded";

    this.shareCardMaker = new ShareableCardMaker();

    this.generatingPoem = false;

    this.supportsWebp = false;

    this.uuid = '';

    if (isCultureStudio) {
      // Lets stylesheets adapt to the Culture Studio host (e.g. its header).
      document.body.classList.add('culture-studio');
      this.shareRewrite.style.display = 'none';
      if (isiOSWebview) {
        // Change the share icon to iOS share icon.
        let shareButton = document.getElementById("studio-share-button");
        let iconElement = shareButton.querySelector('.material-symbols-outlined');
        iconElement.innerText = 'ios_share';
      }
    } else {
      this.studioShareRewrite.style.display = 'none';
    }
  }

  // starts loading the more artworks assets
  preload() {
    this.setPoemTypeSelectionsUi();
  }

  async loadMoreArtworks() {

    this.loadArtworksStatus = "loading";

    let loadingContainer = document.getElementById("more-artworks-loading-container");
    loadingContainer.classList.remove("hidden");

    document.getElementById("more-artworks-loading-spinner").classList.add("spinner");
    document.getElementById("more-artworks-loading-error").classList.add("hidden");

    // Only fetch data and bind event handlers if we didn't get assets from a previous
    // call to this method.
    if (!this.moreArtworksLoaded) {
      let assetsData;

      try {
        assetsData = await this.fetchAssets(this.allAssets);
      } catch(error){
        this.onMoreArtworksLoadError();
        return;
      }

      if (assetsData.hasOwnProperty("error")){
        this.onMoreArtworksLoadError();
        return;
      }
      // load data
      assetsData.items.forEach((item, i) => {
        this.assetsMap.set(item.id, item);
      });

      //---------- Add artworks to ui
      this.setMoreArtworks();

      // --------- Setup pagination
      const paginationList = document.querySelectorAll(".pagination");

      for (const pagination of paginationList) {

        pagination.classList.remove("hidden");

        const paginationButtons =
          pagination.querySelectorAll(".pagination-button");

        for (const paginationButton of paginationButtons) {
          paginationButton.addEventListener(
            "click",
            this.onPaginationButtonClick.bind(this)
          );
        }
      }

      //---- request dominant colors for all artworks
      this.fetchAllColors();

      this.moreArtworksLoaded = true;
    }

    // enable ui
    document.getElementById("select-artwork-button").classList.add("show");

    this.loadArtworksStatus = "loaded";
  }

  async initAxl(axl, initialData, restricted = false) {
    this.axl = axl;

    const queryParams = new URLSearchParams(window.location.search);
    this.assetId = initialData.assetId || this.allAssets[Math.floor(Math.random()*this.allAssets.length)]; // Fall back to random asset.

    // Tracking

    this.axl.sendMessage(AxL.ChildToHost.TRACK, {
      action: 'experiment load',
      category: 'websandbox_ZgG5_uTbBgTpIQ',
      label: this.assetId || 'unset',
    });

    await this.setSupportsWebp();

    this.setAssetImage();

    this.dominantColor = (
      await this.fetchDominantColor(this.assetId)
    ).dominant_color;

    this.setDominantColor(this.dominantColor);

    this.axl.registerMessageCallback(AxL.HostToChild.STUDIO_ACTION, (payload) => {
      const command = payload['c'];
      if (command == 'sa') {
        const newAssetId = payload['a'];
        if (newAssetId != null) {
          this.onSwitchArtworkFromHost(newAssetId);
        }
      }
    });

    const poemFromParams = initialData;
    if (poemFromParams.hasOwnProperty('uuid')) {
      this.createShareablePostcard(poemFromParams, restricted);
      return;
    }
  }

  async init(data, axl) {
    this.allAssets = await this.getAllAssets();

    this.poemCleaned = "";

    // Event listeners
    this.toneInput.addEventListener("keydown", this.onToneInputKeyDown);
    this.generateButton.addEventListener("click", this.onGenerateButtonClicked);
    this.rewriteButton.addEventListener("click", this.onBackToEditButtonClicked);
    this.studioRewriteButton.addEventListener("click", this.onBackToEditButtonClicked);
    this.backToEdit.addEventListener("click", this.onBackToEditButtonClicked);

    this.initShared();

    this.moreArtButton.addEventListener(
      "click",
      this.onMoreArtworksButtonClicked
    );
    this.shareMoreArtButton.addEventListener(
      "click",
      this.onMoreArtworksButtonClicked
    );
    this.selectArtworkButton.addEventListener(
      "click",
      function () {
        this.onSelectedNewArtwork();
      }.bind(this)
    );
    this.poemTypeSelect.addEventListener(
      "click",
      function (e) {
        this.showPage("poem-type-select-page");

        const poemType = document
          .querySelector("#poem-type-selected-name")
          .getAttribute("data-value");
        const selectionEl = document
          .querySelector(`.poem-type-selection[data-value="${poemType}"]`)
          .parentNode.parentNode;
        if (selectionEl) {
          this.setExpanded(selectionEl, e);
        }
      }.bind(this)
    );
    this.closePoemErrorButton.addEventListener(
      "click",
      function(e) {
        this.poemErrorMessage.classList.add("hidden");
      }.bind(this)
    );

    document.getElementById("create-new-button").addEventListener("click",
      function(e){
        this.onBackToEditButtonClicked(e);
      }.bind(this));

    window.addEventListener(
      "resize",
      function () {
        this.resizeImage();
      }.bind(this)
    );

    document
      .querySelector("#artwork-poem-view-cover-nav-rewrite")
      .addEventListener(
        "click",
        function () {
          this.onBackToEditButtonClicked();
        }.bind(this)
      );

    let backToPageButtons = document.querySelectorAll(".back-to-page");
    backToPageButtons.forEach((button, i) => {
      button.addEventListener(
        "click",
        function () {
          this.showPage(this.prevPage.getAttribute("id"));
        }.bind(this)
      );
    });

    let backToEditButtons = document.querySelectorAll(".back-to-edit");
    backToEditButtons.forEach((button, i) => {
      button.addEventListener(
        "click",
        function () {
          this.onBackToEditButtonClicked();
        }.bind(this)
      );
    });

    // links
    let axlLinks = document.querySelectorAll("span.clickable");
    let wikipediaLinks = document.querySelectorAll(".clickable.wikipedia-link");

    [...axlLinks, ...wikipediaLinks].forEach((link, i) => {
      link.addEventListener('click', function(e){
        let url = e.currentTarget.getAttribute("data-url");
        if(this.axl){
          this.axl.sendMessage(AxL.ChildToHost.NAVIGATE, url);
        }
      }.bind(this));

    });

    // ---o Popins
    const buttonPopinList = document.querySelectorAll(".button-popin");
    buttonPopinList.forEach((buttonPopin) => {
      buttonPopin.addEventListener("click", (e) => {
        const targetPopin = e.currentTarget.getAttribute("data-target");
        document.querySelector(`.popin#${targetPopin}`).classList.add("active");
      });
    });

    const buttonPopinCloseList = document.querySelectorAll(
      ".button-popin-close"
    );
    buttonPopinCloseList.forEach((buttonPopinClose) => {
      buttonPopinClose.addEventListener("click", (e) => {
        const targetPopin = e.currentTarget.closest(".popin");
        targetPopin.classList.remove("active");
      });
    });

    const popinList = document.querySelectorAll(".popin");
    popinList.forEach((popin) => {
      popin.addEventListener("click", (e) => {
        if (e.target.className === "popin active") {
          e.target.classList.remove("active");
        }
      });
    });

    let buttonsWithColor = document.querySelectorAll(".ripple-button");
    buttonsWithColor.forEach((button, i) => {
      button.addEventListener('click',this.onButtonClick);
    });

    if (mobile()) {
      document.getElementById("landscape-lock-screen").classList.add("show");
    }

    document.getElementById("poem-loading-cancel").addEventListener("click", function() {
      this.cancelPoemLoading();
    }.bind(this));

    this.studioSaveButton.addEventListener(
      "click",
      function (e) {
        this.savePoem(e);
      }.bind(this)
    );

    this.preload();

    if (!!window['_surgeMode']) {
      this.showMessage("popular");
    }
  }

  async initShared() {
    let shareButtons = document.querySelectorAll("[data-target='share']");
    shareButtons.forEach((button, i) => {
      button.addEventListener("click", this.onShareButtonClicked);
    });

    document.querySelector(".poem-back-to-cover").addEventListener(
      "click",
      function () {
        this.showPage("artwork-cover");
      }.bind(this)
    );

    document
    .querySelector("#artwork-poem-view-cover-nav-show")
    .addEventListener(
      "click",
      function () {
        this.showPage("poem-view-page");
      }.bind(this)
    );

    this.configureCaptionLinks();
  }

  async initRestricted(axl, initialData) {
    this.axl = axl;

    if (window.navigator.userAgent.includes('SMLGWebView')) {
      document.getElementById("goto-arts-button").classList.add('hidden');
    } else {
      document.getElementById("goto-arts-button").addEventListener('click', function(){
        if(this.axl){
          this.axl.sendMessage(AxL.ChildToHost.NAVIGATE, "https://artsandculture.google.com");
        }
      }.bind(this));
    }

    const queryParams = new URLSearchParams(window.location.search);
    this.assetId = initialData.assetId || "bgEuwDxel93-Pg"; // Fall back to Starry Night

    this.setAssetImage();

    this.configureCaptionLinks();

    this.dominantColor = (
      await this.fetchDominantColor(this.assetId)
    ).dominant_color;

    this.setDominantColor(this.dominantColor);
  }

  //---- set main artowrk and captions

  async resizeImage() {
    if (this.assetId) {
      const artworkDetails = await this.fetchArtworkDetails(this.assetId);
      if (artworkDetails) {
        let imageUrl = this.getFifeCropUrl(
          artworkDetails.image.url,
          this.artworkImg
        );
        this.tempImage = new Image();
        await this.loadImageToElement(imageUrl, this.tempImage).catch(
          function(){
              console.log("couldn't get image");
              this.tempImage = null;
          }.bind(this)
        );
        if(this.tempImage != null){
          console.log("got image", this.tempImage.src);
          this.artworkImg.src = this.tempImage.src;
        }

      }
    }
  }

  async setAssetImage() {
    const artworkDetails = await this.fetchArtworkDetails(this.assetId);

    this.setImageCaptions(this.assetId,artworkDetails);

    let imageUrl = this.getFifeCropUrl(
      artworkDetails.image.url,
      this.artworkImg
    );

    await this.loadImageToElement(imageUrl, this.artworkImg);
    this.artworkImg.classList.remove("hide");

    let poemViewSection = document.getElementById("poem-view-section-background");
    if(poemViewSection)
      poemViewSection.style.backgroundImage = "url(" + this.artworkImg.src + ")";

    let artworkCoverBG =  document.getElementById("artwork-cover-background");
    if(artworkCoverBG)
      artworkCoverBG.style.backgroundImage ="url(" + this.artworkImg.src + ")";

    await this.shareCardMaker.storeImageForShareable(this.assetId, artworkDetails.image.url);

  }

  configureCaptionLinks() {
    const captionLinkEls = document.querySelectorAll(".caption-title");
    captionLinkEls.forEach((item, i) => {
      item.addEventListener(
        "click",
        function (e) {
          const assetId = e.currentTarget.getAttribute("data-assetId");
          if(assetId){
            const captionUrl = `asset/${assetId}`;
            if(this.axl){
              this.axl.sendMessage(AxL.ChildToHost.NAVIGATE, captionUrl);
            }
          }

        }.bind(this)
      );
    });
  }

  getFifeCropUrl(base_url, el) {
    let bb = el.parentNode.getBoundingClientRect();
    let w = Math.floor(el.width * 1.5);
    let h = Math.floor(el.height * 1.5);
    let requestWebp = this.supportsWebp ? "-rw" : "";
    return base_url + `=w${w}-h${h}-dc${requestWebp}-v1`;
  }

  setImageCaptions(assetId, artworkDetails){
    let titles = document.querySelectorAll(".caption-title");
    let partners = document.querySelectorAll(".caption-partner");
    let authors = document.querySelectorAll(".caption-artist");

    titles.forEach((item, i) => {
      item.innerText = artworkDetails.title;
      item.setAttribute("data-assetId",assetId);
    });

    authors.forEach((item, i) => {
      if (artworkDetails.creators.length > 0)
        item.innerText = artworkDetails.creators[0].title;
      else
        item.innerText = "";
    });

    partners.forEach((item, i) => {
      item.innerText = artworkDetails.partner;
    });

  }

  async loadImageToElement(src, img) {
    return new Promise((resolve, reject) => {
      // let img = document.getElementById("painting-img");
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = src;
    });
  }

  setSupportsWebp = async () => {
    const img = new Image();
    const BASE64_LOSSY_WEBP = 'data:image/webp;base64,' +
    'UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA';
    return new Promise((resolve, reject) => {
      img.onload = () => {
        this.supportsWebp = img.width > 0 && img.height > 0;
        resolve();
      };
      img.onerror = () => {
        this.supportsWebp = false;
        reject();
      };
      img.src = BASE64_LOSSY_WEBP;
    })

  }


  //------- Set Ui
  setPoemTypeSelectionsUi() {

    let buttons = document.querySelectorAll('.poem-type-more');
    buttons.forEach((buttonEl, i) => {
      buttonEl.addEventListener(
        "click",
        function (e) {
          let grandparent = e.currentTarget.parentNode.parentNode;
          this.toggleExpanded(grandparent,e);
        }.bind(this)
      );
    });


    let containers = document.querySelectorAll('.poem-type-selection');
    containers.forEach((containerEl, i) => {
      containerEl.addEventListener(
        "click",
        function (e) {
          this.toggleExpanded(e.currentTarget.parentNode.parentNode,e);

          // this.setExpanded(e.currentTarget);
        }.bind(this)
      );
    });


    let expandeds = document.querySelectorAll('[data-target="poem-type-select"]');
    expandeds.forEach((expandEl, i) => {
      expandEl.addEventListener(
        "click",
        function (e) {
          let parent = e.currentTarget.parentNode.parentNode;
          document
            .getElementById("poem-type-selected-name")
            .setAttribute("data-value", parent.getAttribute("data-value"));
          document
            .getElementById("poem-type-selected-name")
            .setAttribute("data-loading-text", parent.getAttribute("data-loading-text"));

          document.getElementById("poem-type-selected-name").innerText =
            parent.getAttribute("data-name");
          this.showPage("main-content-view");
        }.bind(this)
      );
    });

  }

  toggleExpanded(el,event){
    event.stopPropagation();

    if(el.classList.contains("expanded")){
      el.classList.remove("expanded");
    }else{
      this.setExpanded(el);
    }
  }

  setExpanded(el) {

    if (el.classList.contains("expanded")) return;
    let currExpand = document.querySelectorAll("#poem-types-list .expanded");
    currExpand.forEach((item, i) => {
      item.classList.remove("expanded");
    });

    el.classList.add("expanded");
    // el.firstChid.classList.add("expanded");
  }

  //--- utils
  isMobilePortrait() {
    if (window.innerWidth < window.innerHeight &&  window.innerWidth < 920) {
      return true;
    }
    return false;
  }

  sanitizeUserInput(text){
    return text.replace(/[^a-zA-Z0-9\s\.]+/g,"");
  }

  //------- More Artworks
  setMoreArtworks() {
    let parent = document.querySelector(
      "#more-artworks .pagination-target"
    );

    for (let [assetId, assetData] of this.assetsMap) {
      if (Object.keys(assetData).length > 0) {


        let li = document.createElement("li");

        let imgDiv = document.createElement("div");
        const fifeResizedUrl = assetData.image.url.replace(
          "s1200",
          "h150-c-w150"
        );
        imgDiv.setAttribute("data-src", fifeResizedUrl);

        let artworkLabel = assetData.title;
        if (assetData.creators.length > 0) {
          artworkLabel += ', ' + assetData.creators[0].title;
        }
        imgDiv.setAttribute("aria-label", artworkLabel)

        imgDiv.setAttribute("tabindex", 0)
        imgDiv.classList.add("img");
        li.appendChild(imgDiv);

        li.setAttribute("data-assetid", assetId);

        parent.appendChild(li);

        li.addEventListener("click", (e) => {
          this.showMoreArtwork(e.currentTarget.getAttribute("data-assetid"));
        });

        // this.loadImages(li);

        if (parent.children.length === 1 && !this.isMobilePortrait()) {
          this.showMoreArtwork(assetId);
        }
      }
    }

    this.loadImages(parent);

    let loading = document.querySelectorAll(".more-artworks-loading");
    loading.forEach((item, i) => {
      item.classList.add("hidden");
    });
  }

  onMoreArtworksLoadError(){

    this.loadArtworksStatus = "error";

    let loadingContainer = document.getElementById("more-artworks-loading-container");
    loadingContainer.classList.add("hidden");

    document.getElementById("more-artworks-loading-spinner").classList.remove("spinner");
    document.getElementById("more-artworks-loading-error").classList.remove("hidden");

  }

  onPaginationButtonClick(e) {
    const parent = e.currentTarget.parentNode;
    const pages = parent.querySelectorAll("li");
    const currentPage = e.currentTarget.parentNode.querySelector("li.active");
    const currentPageIndex = parseInt(currentPage.innerText);
    const lastPageIndex = parseInt(pages[pages.length - 1].innerText);
    const isPrev = e.currentTarget.classList.contains("pagination-button-prev");

    if (
      (isPrev && currentPageIndex === 1) ||
      (!isPrev && currentPageIndex === lastPageIndex)
    ) {
      return;
    }

    const newIndex = currentPageIndex + (isPrev ? -1 : 1);
    currentPage.classList.remove("active");
    pages[newIndex - 1].classList.add("active");

    const buttonPrev = parent.querySelector(".pagination-button-prev");
    const buttonNext = parent.querySelector(".pagination-button-next");

    if (newIndex > 1 && buttonPrev.classList.contains("disabled")) {
      buttonPrev.classList.remove("disabled");
    } else if (newIndex === 1 && !buttonPrev.classList.contains("disabled")) {
      buttonPrev.classList.add("disabled");
    }

    if (newIndex < lastPageIndex && buttonNext.classList.contains("disabled")) {
      buttonNext.classList.remove("disabled");
    } else if (
      newIndex === lastPageIndex &&
      !buttonNext.classList.contains("disabled")
    ) {
      buttonNext.classList.add("disabled");
    }

    const target = parent.parentNode.querySelector(".pagination-target");
    target.setAttribute("data-page", newIndex - 1);

    this.loadImages(target);
  }

  async onSwitchArtworkFromHost(assetId) {
    this.assetId = assetId;
    this.handleSwitchingArtwork();
  }

  async onSelectedNewArtwork() {
    const activeEl = document.querySelector("#more-artworks .active");
    this.assetId = activeEl.getAttribute("data-assetid");
    this.handleSwitchingArtwork();
  }

  async handleSwitchingArtwork() {
    if(this.axl){
      this.axl.sendMessage(AxL.ChildToHost.TRACK, {
        action: 'click artwork',
        label: this.assetId
      });
    }

    this.artworkImg.classList.add("hide");

    const artworkDetails = await this.fetchArtworkDetails(this.assetId);

    if (artworkDetails["dominantColor"] != null) {
      this.dominantColor = artworkDetails["dominantColor"];
      this.setDominantColor(this.dominantColor);
    } else {
      let assetId = this.assetId;
      let dColor = (
        await this.fetchDominantColor(assetId)
      ).dominant_color;
      this.assetsMap.set(assetId, {
        ...this.assetsMap.get(assetId),
        dominantColor: dColor,
      });
      if(this.assetId == assetId){
        this.setDominantColor(dColor);
      }
      // this.dominantColor = (
      //   await this.fetchDominantColor(this.assetId)
      // ).dominant_color;
    }



    this.setImageCaptions(this.assetId,artworkDetails);

    this.showPage("main-content-view");
    let image_url = this.getFifeCropUrl(
      artworkDetails.image.url,
      this.artworkImg
    );

    await this.loadImageToElement(image_url, this.artworkImg);
    this.artworkImg.classList.remove("hide");

    document.getElementById(
      "poem-view-section-background"
    ).style.backgroundImage = "url(" + this.artworkImg.src + ")";

    document.getElementById("artwork-cover-background").style.backgroundImage =
      "url(" + this.artworkImg.src + ")";

    await this.shareCardMaker.storeImageForShareable(this.assetId, artworkDetails.image.url);

  }

  async showMoreArtwork(assetId) {
    const clickedItem = document.querySelector(
      `#more-artworks [data-assetid="${assetId}"`
    );
    const previousClickedItem = clickedItem.parentNode.querySelector(".active");
    if (previousClickedItem) {
      previousClickedItem.classList.remove("active");
    }
    clickedItem.classList.add("active");

    const data = this.assetsMap.get(assetId);
    document.getElementById("more-artworks-title").innerText = data.title;
    if (data.creators.length > 0)
      document.getElementById("more-artworks-author").innerText =
        data.creators[0].title;
    else {
      document.getElementById("more-artworks-author").innerText = "";
    }
    document.getElementById("more-artworks-partner").innerText = data.partner;
    //

    // get dominat color when selecting
    if (
      !data.hasOwnProperty("dominantColor") ||
      data["dominantColor"] == null
    ) {
      let dColor = (await this.fetchDominantColor(assetId)).dominant_color;
      this.assetsMap.set(assetId, {
        ...this.assetsMap.get(assetId),
        dominantColor: dColor,
      });
    }
    this.setDominantColor(this.assetsMap.get(assetId)['dominantColor']);


    if (this.isMobilePortrait()) {
      this.onSelectedNewArtwork();
    }
  }

  setDominantColor(dominantColor) {
    this.dominantColor = dominantColor;

    const [hue, saturation, lightness] = hexToHsl("#" + this.dominantColor);
    let rgbVals = hslToRgb(hue,saturation,25);
    let rgb = {r:rgbVals[0],g:rgbVals[1],b:rgbVals[2]};

    document.body.style.setProperty(
      "--color-main",
      `hsl(${hue} ${saturation}% ${LIGHTNESS})`
    );

    document.body.style.setProperty(
      "--color-light",
      `hsl(${hue} ${saturation}% ${LIGHTNESS} / ${BACKGROUND_ALPHA})`
    );

    document.body.style.setProperty(
      "--color-dark",
      `hsl(${hue} ${saturation}% ${LIGHTNESS_BUTTON})`
    );

    document.body.style.setProperty(
      "--color-medium",
      `hsl(${hue} ${saturation}% ${LIGHTNESS_BUTTON} / ${BACKGROUND_ALPHA} )`
    );

    document.getElementById("artwork-scrim-layer1").style.setProperty(
      "--scrim-background",
      `linear-gradient(180deg,
        rgba(${rgb.r}, ${rgb.g}, ${rgb.b},  .2 ) 20%,
        rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0 ) 40%,
        rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0 ) 60%,
        rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, .4 ) 80%)
      `
    );

    if(this.stampAmpersand){
      this.stampAmpersand.setAttribute(
        "fill",
        `hsl(${hue} ${saturation}% ${LIGHTNESS})`
      );

      this.stampAmpersandMobile.setAttribute(
        "fill",
        `hsl(${hue} ${saturation}% ${LIGHTNESS})`
      );
    }

  }

  loadImages(container, width = 150, height = 150) {
    const imgList = container.querySelectorAll("[data-src]");
    for (const img of imgList) {
      const src = img.getAttribute("data-src");
      if (src) {
        const fifeResizedUrl = src.replace("s1200", `h${height}-c-w${width}`);
        if (height !== 0 && width !== 0) {
          const imgToLoad = new Image();
          imgToLoad.crossOrigin = "anonymous";
          imgToLoad.onload = () => {
            img.style.backgroundImage = `url('${fifeResizedUrl}')`;
            img.classList.add("loaded");
            img.setAttribute("data-src", "");
          };
          imgToLoad.src = fifeResizedUrl;
        }
      }
    }
  }

  async getAllAssets() {
    try{
      const url = `${
        window.location.origin +
        (window.location.pathname === "/" ? "" : window.location.pathname)
      }/getAllAssets`;
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        const message = response.status;
        throw new Error(message);
      } else {
        let responseData = await response.json();
        return responseData.assets;
      }
    }catch(err){
      console.log("Error: ",err.name);
      return {error:"could not get assets", type:err.name};
    }

  }

  //------- Page changes
  showPage(id) {

    this.prevPage = document.querySelector(".page.active");
    this.prevPage.classList.remove("active");

    this.currPage = document.getElementById(id);
    this.currPage.classList.add("active");

    if(this.currPage.id == "more-artworks"){
        this.prevPage.classList.add("flipped");
        this.currPage.classList.add("flipped");
    }else if(this.currPage.id == "artwork-cover"){
        this.prevPage.classList.add("flipped");
        this.currPage.classList.add("flipped");
        document.getElementById("main-content-view").classList.add("hidden");
    }else if(this.prevPage.id == "artwork-cover" && this.currPage.id == "main-content-view"){
        this.prevPage.classList.remove("backside");
        this.prevPage.classList.remove("flipped");
        this.prevPage.classList.add("slide-page");
    }else{
        this.prevPage.classList.remove("flipped");
        this.currPage.classList.remove("flipped");
    }

    if (this.currPage.id == "main-content-view") {
      this.currPage.classList.remove("hidden");
    }

    if(this.currPage.id == "poem-view-page"){
      this.currPage.classList.remove('flipped');
      document.getElementById("artwork-cover").classList.add("backside");
      document.getElementById("artwork-cover").classList.remove("hidden");
    }
    // slide main page section
    if (
      this.currPage.classList.contains("slide-page") &&
      this.prevPage.id == "main-content-view"
    ) {
      let selector = "#" + this.prevPage.id + " .page-content";
      let slideSection = document.querySelector(selector);
      slideSection.classList.add("slide-out");
      this.prevPage.classList.add("disabled");
    } else if (this.currPage.id == "main-content-view") {
      let selector = "#" + this.currPage.id + " .page-content";
      let slideSection = document.querySelector(selector);
      slideSection.classList.remove("slide-out");
      this.currPage.classList.remove("disabled");
    }

    if (this.prevPage.id == "more-artworks") {
      this.setDominantColor(this.dominantColor);
      let dColor = this.assetsMap.get(this.assetId)['dominantColor'];
      if(dColor != null)
        this.setDominantColor(dColor);
    }


  }

  //------- User Interactions

  onButtonClick(e) {
    const button = e.currentTarget;
    const buttonBounds = button.getBoundingClientRect();
    const posX = e.pageX - buttonBounds.left;
    const posY = e.pageY - buttonBounds.top;

    new Ripple(button, posX, posY);
  }

  onMoreArtworksButtonClicked = (event) => {
    // if(this.loadArtworksStatus == "error" || this.loadArtworksStatus == "notloaded"){
    //   console.log("try load art");
    // }

    if (isCultureStudio && this.axl) {
      this.sendStudioAction('ma')
      return;
    }

    this.loadMoreArtworks();

    this.showPage("more-artworks");

    if(this.axl){
      this.axl.sendMessage(AxL.ChildToHost.TRACK, {
        action: 'click more artworks'
      });
    }

  };

  onToneInputKeyDown = async (event) => {
    if (event.key) {
      if (event.key === "Enter") {
        this.toneInput.blur();
        if ( !mobile() ) {
          this.onGenerateButtonClicked();
        }
      }
    }
  }

  onGenerateButtonClicked = async (event) => {

    this.toneInput.blur();

    if (this.generatingPoem) {
      return;
    }

    this.generatingPoem = true;

    document.getElementById("poem-view-page").classList.remove("flipped");
    document.getElementById("artwork-cover").classList.remove("slide-page");
    document.getElementById("artwork-cover").classList.add("hidden");

    let buttonEl = this.generateButton;

    // start loading screen
    this.showPoemLoadingUI();

    this.toneWarning.classList.add("hidden");
    this.poemErrorMessage.classList.add("hidden");

    const assetId = this.assetId;
    const poemType = document
      .querySelector("#poem-type-selected-name")
      .getAttribute("data-value");

    if(this.axl){
      let value = (this.toneInput.value.length == 0) ? 0 : 1;
      this.axl.sendMessage(AxL.ChildToHost.TRACK, {
        action: 'click write a poem',
        label: poemType + "-" + this.assetId,
        value: value
      });
    }

    const artworkDetailsPromise = this.fetchArtworkDetails(assetId);
    const tone = this.sanitizeUserInput(this.toneInput.value);

    const poemResponse = await this.fetchPoem(
      tone,
      poemType,
      assetId
    );

    console.log("poemResponse",  poemResponse);

    // error handle
    if(poemResponse.hasOwnProperty('error')){

      // handle error from user canceled
      if(poemResponse.type == "cancel"){
        buttonEl.classList.remove("spinner");
        this.generatingPoem = false;
        return;
      }

      // handle error with message
      let type = "default";

      if(poemResponse.hasOwnProperty("message")){
        if(poemResponse.message == "inputSafety"){
          buttonEl.classList.remove("spinner");
          this.toneWarning.classList.remove("hidden");
          this.closePoemLoadingUI();
          this.generatingPoem = false;
          return;
        }
      }

      if(poemResponse.type == "AbortError")
        type = "generic";

      this.handlePoemFetchError(buttonEl,type);
      this.generatingPoem = false;

      return;

    }

    // response received

    const { poem, uuid } = poemResponse;

    const { creators, title, partner } = await artworkDetailsPromise;
    this.updateAttribution(title);

    this.poemCleaned = this.getCleanedPoemText(poem);

    this.poem.innerHTML = this.poemCleaned.replace(/\n/g, "<br>");

    document.getElementById("poem-view-page").classList.remove("shared-view","flipped");
    this.showPage("poem-view-page");
    this.closePoemLoadingUI();

    buttonEl.classList.remove("spinner");
    this.uuid = uuid;
    this.generatingPoem = false;
  };

  showPoemLoadingUI(){
    const poemLoadingText = document
      .querySelector("#poem-type-selected-name")
      .getAttribute("data-loading-text");

    let attribution = document.getElementById("loading-poem-text");

    attribution.innerText = poemLoadingText;

    document.getElementById("poem-select-section").classList.add("loading");
    document.getElementById("loading-poem-spinner").classList.add("spinner");

  }

  closePoemLoadingUI(){
    document.getElementById("poem-select-section").classList.remove("loading");
    setTimeout(
      ()=>{document.getElementById("loading-poem-spinner").classList.remove("spinner");},
      500
    );
  }

  cancelPoemLoading(){
    console.log("cancel");
    // abort call
    if(this.poemSignal){
      this.poemSignal.abort("cancel");
    }
    // close ui
    this.closePoemLoadingUI();
  }

  showMessage(type, timeout) {
    this.poemErrorMessage.setAttribute("data-type",type);
    this.poemErrorMessage.classList.remove("hidden");

    if (timeout) {
      setTimeout(function(){
        this.poemErrorMessage.classList.add("hidden");
      }.bind(this), timeout);
    }
  }

  handlePoemFetchError(buttonEl,type="default"){
    this.closePoemLoadingUI();
    buttonEl.classList.remove("spinner");
    this.showMessage(type, 5000);
  }

  onBackToEditButtonClicked = async (event) => {

    this.poem.innerHTML = "";

    this.showPage("main-content-view");

  };

  updateAttribution(title) {
    let attributions = document.querySelectorAll(".poem-attribution");
    attributions.forEach((item, i) => {
      let attributionParts = [];
      var template = item.getAttribute('data-attribution-no-tone');
      attributionParts.push(title);
      for (const item of attributionParts) {
        template = template.replace("{}", `${item}`);
      }
      item.innerText = template;
    });
  }

  getCleanedPoemText(poem) {
    let poemCleaned = poem
      .replace(/Sure.*/, "")
      .replace(/Here is a.*/, "")
      .trim(); // Remove Bard saying "Sure, here is ..."
    // poemCleaned = poemCleaned.replaceAll("*", "");
    poemCleaned = poemCleaned.replace(/[`~@#$%^&*_+={}\[\]|<>]/gm,"");
    // poemCleaned.replace(/\n/g, "<br>");
    return poemCleaned;
  }

  onShareButtonClicked = async (event) => {

    if(this.axl){
      this.axl.sendMessage(AxL.ChildToHost.TRACK, {
        action: 'click share poem',
        label: this.assetId
      });
    }

    const urlParams = {
      assetId: this.assetId,
      uuid: this.uuid
    };

    if(this.axl){
      this.axl.sendMessage(AxL.ChildToHost.SET_URL_PARAMS, urlParams);
    }

    let partners = document.querySelectorAll(".caption-partner");
    let authors = document.querySelectorAll(".caption-artist");

    let partner = partners.length > 0 ? partners[0].innerText : null;
    let author = authors.length > 0 ? authors[0].innerText : "unknown";

    let studioShareString = partner
        ? "I just created this Poem Postcard in my Culture Studio: inspired by " + author + " from " + partner +  ". Check it out then create your own: "
        : "I just created this Poem Postcard in my Culture Studio.  Check it out then create your own: ";

    // This ends up in the email *subject*
    const shareText = isCultureStudio ? studioShareString : "You've Received a Poem Postcard";

    // This ends up in the email *body*
    const shareTitle = isCultureStudio ? studioShareString : "Read your Poem Postcard on Google Arts & Culture: ";

    if(AxL.can["shareFiles"]){
      let shareImg = this.shareCardMaker.makeShareableCard(this.dominantColor,this.assetId).split(",")[1];
      this.axl.sendMessage(AxL.ChildToHost.SHARE, {
        title: shareTitle,
        // title: shareUrl,
        text: shareText,
        // text: shareUrl,
        // href: shareUrl,
        // TODO(koenraad): figure out a way how to share files together with the link. Currently if files are shared then the link is not in SMLG.
        // files: [{ data: shareImg, name: "poempostcard.png", type: "image/png" }],
      });
    }else if(this.axl){
      this.axl.sendMessage(AxL.ChildToHost.SHARE, {
        title: shareTitle,
        // title: shareUrl,
        text: shareText,
        // text: shareUrl,
        // href: shareUrl,
      });
    }
    // testing
    // let shareImg = this.shareCardMaker.makeShareableCard(this.dominantColor,this.assetId);
    // this.shareCardMaker.downloadCard();
  };

  // Propagate the asset ID and the poem itself.
  savePoem(e) {
    e.stopPropagation();
    if (isCultureStudio && this.axl) {
      const urlParams = {
        assetId: this.assetId,
        uuid: this.uuid
      };
      this.axl.sendMessage(AxL.ChildToHost.SET_URL_PARAMS, urlParams);
      this.sendStudioAction('sc', {
        't': this.poemCleaned,
        'a': this.assetId,
      });
    }
  };

  //------- Poem Generation
  fetchPoem = async (mood, poemType, assetId ) => {

    if(CS_DEBUG){
      return {poem:"The stars are swirling,\nThe sun is fiery,\nThe moon is a smiley face.\n\nVan Gogh, you were a genius,\nYour art is amazing,\nIt makes me laugh and it makes me think.\n\nThank you for sharing your vision,\nWith the world,\nWe are all grateful."};
    }

    this.poemSignal = new AbortController();

    const timer = setTimeout( function(){ this.poemSignal.abort(); }.bind(this), 30*1000);

    const url = `${window.location.origin +(window.location.pathname === "/" ? "" : window.location.pathname)}/poemv3`;

    try{
      const response = await fetch(
        url,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ word: mood, poemType, assetId }),
          signal: this.poemSignal.signal//AbortSignal.timeout(30*1000)
        }
      );

      clearTimeout(timer);
      const jsonData = await response.json();
      return jsonData;
    }catch(err){
      let type = err.name;
      clearTimeout(timer);
      if(this.poemSignal.signal.aborted){
        if(this.poemSignal.signal.reason == "cancel")
          type = "cancel";
      }
      console.log("Poemv3 Error: ",err.name,err.message,err.reason);
      return {error:"could not get poem", type: type};
    };

  }

  async fetchAllColors(){
    let keysList = [ ...this.assetsMap.keys() ];

    const response = await fetch(`${
      window.location.origin +
      (window.location.pathname === "/" ? "" : window.location.pathname)
    }/dominant-colors`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(keysList),
    });
    const jsonData = await response.json();
    console.log(jsonData);

    keysList.forEach((assetId, i) => {
      this.assetsMap.set(assetId, {
        ...this.assetsMap.get(assetId),
        dominantColor: jsonData['dominant_colors'][assetId],
      });
    });



    return jsonData;

  }

  //------- Asset data
  async fetchArtworkDetails(assetId) {
    const existingData = this.assetsMap.get(assetId);
    if (existingData != undefined) {
      return existingData;
    }

    const response = await fetch(
      `${
        window.location.origin +
        (window.location.pathname === "/" ? "" : window.location.pathname)
      }/og/${assetId}`
    );
    const jsonData = await response.json();
    jsonData["dominantColor"] = null;
    this.assetsMap.set(assetId, jsonData);
    return jsonData;
  }

  fetchAssets = async (assetIds) => {
    try{
      const response = await fetch(
        `${
          window.location.origin +
          (window.location.pathname === "/" ? "" : window.location.pathname)
        }/getAssetsData`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ assetIds: assetIds}),
          signal: AbortSignal.timeout(30*1000)

        }
      );
      const jsonData = await response.json();
      return jsonData;
    }catch(err){
      console.log("Assets Data Error: ",err.name);
      return {error:"assets data load failed", type:err.name};
    }
  };

  async fetchDominantColor(assetId) {
    const response = await fetch(
      `${
        window.location.origin +
        (window.location.pathname === "/" ? "" : window.location.pathname)
      }/dominant-color`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ assetId }),
      }
    );
    const jsonData = await response.json();
    return jsonData;
  }

  async getPoem(uuid){
    try{
      const response = await fetch(
        `${
          window.location.origin +
          (window.location.pathname === "/" ? "" : window.location.pathname)
        }/getPoem`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ 'uuid':uuid}),
          signal: AbortSignal.timeout(30*1000)
        }
      );
      const jsonData = await response.json();
      return jsonData.data;
    }catch(err){
      console.log("Get Poem Error: ",err.name);
      return {error:"could not get shared poem", type:err.name};
    }
  }

  //----- Show shared view
  createShareablePostcard = async (poemData, restricted = false) => {

    try{
      let uuid = poemData.uuid;

      let poemJson = await this.getPoem(uuid);

      if(poemJson.hasOwnProperty('error')){
        console.log("aborted, could not get shared poem");
        return;
      }
      this.poemCleaned = poemJson.poem;

      this.poem.innerHTML = poemJson.poem.replace(/\n/g, "<br>");

      this.updateAttribution(poemJson.title);


    }catch(err){
      console.log("could not get shared poem");
      return;
    }


    if(this.isMobilePortrait()){
      this.showPage("poem-view-page");
      this.showPage("artwork-cover");
    }else{
      this.showPage("poem-view-page");
    }

    document.getElementById("artwork-cover").classList.add("shared-view");
    document.getElementById("poem-view-page").classList.add("shared-view");

    if (restricted) {
      document.getElementById("artwork-cover").classList.add("restricted");
      document.getElementById("poem-view-page").classList.add("restricted");
    }
  };

 sendStudioAction(command, params = {}) {
    params['c'] = command;
    this.axl.sendMessage(AxL.ChildToHost.STUDIO_ACTION, params);
  }
}

const hexToHsl = (hex) => {
  // See https://stackoverflow.com/questions/3732046/how-do-you-get-the-hue-of-a-xxxxxx-colour
  var r = parseInt(hex.substr(1, 2), 16); // Grab the hex representation of red (chars 1-2) and convert to decimal (base 10).
  var g = parseInt(hex.substr(3, 2), 16);
  var b = parseInt(hex.substr(5, 2), 16);
  (r /= 255), (g /= 255), (b /= 255);
  var max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  var h,
    s,
    l = (max + min) / 2;

  if (max == min) {
    h = s = 0; // achromatic
  } else {
    var d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return [360 * h, 100 * s, 100 * l];
};

const hslToRgb = (h, s, l) => {
  s /= 100;
  l /= 100;
  const k = n => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = n =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [255 * f(0), 255 * f(8), 255 * f(4)];
};
